import React, { useMemo, useState } from "react";
import { useStaticQuery, graphql, navigate } from "gatsby";
import axios from "axios";
import styled, { css } from "styled-components";
import Grid from "./Grid";
import { FormField, FormWrapper } from "./Form";
import CallTrackLink from "./CalltrackLink";
import ContactTelephoneWrapper from "./ContactTelephoneWrapper";
import { getGAClientId } from '../utils/getGAClientId';
import randomstring from "randomstring";
import { HtmlContent } from "./Content";
import { FileInput } from "./FileInput";
import { useSiteSettings } from "../context/SiteSettingsContext";

const ButtonStyles = styled.button`
  ${({ ...props }) => css`
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15), 0 2px 2px rgba(0, 0, 0, 0.15),
      0 4px 4px rgba(0, 0, 0, 0.15), 0 8px 8px rgba(0, 0, 0, 0.15);
    min-width: 200px;
  `}
`;

const FormFooterStyles = styled.div`
  ${({ ...props }) => css`
    display: grid;
    column-gap: 16px;
    row-gap: 24px;
    margin-top: 8px;
    a {
      color: ${props.theme.colors.primary};
    }
    @media (min-width: ${props.theme.breakpoints.sm}) {
      grid-template-columns: ${(props.$inline ? '1fr 1fr' : 'auto')};
    }
    @media (min-width: ${props.theme.breakpoints.xl}) {
      display: ${(props.$inline ? "flex" : "grid")};
      justify-content: space-between;
      align-items: center;
    }
  `}
`;

export const QuoteForm = ({ inline, source }) => {
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");

  let siteData = {
    wp: {
      siteSettings: {
        siteSettings: {
          quoteFormHeading: 'Request a free no-obligation quote',
          quoteFormSubheading: 'We respond in under 30 mins on average (excl. weekends)',
        }
      }
    }
  }
  if (!process.env.STORYBOOK) {
    siteData = useStaticQuery(graphql`
      query QuoteFormQuery {
        wp {
          siteSettings {
            siteSettings {
              quoteFormHeading
              quoteFormSubheading
            }
          }
        }
      }
    `);
  }

  const [serverState, setServerState] = useState({
    submitting: false,
    status: null,
  });
  
  const handleServerResponse = (ok, msg, form) => {
    setServerState({
      submitting: false,
      status: { ok, msg },
    });
    if (ok) {
      setFiles([]);
      setFileError("");
      form.reset();
    }
  };

  const { quoteRequestFormEndpoint } = useSiteSettings();

  const handleOnSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    setServerState({ submitting: true });

    const formDataWithFiles = new FormData(form);
    files.forEach(file => formDataWithFiles.append("file", file));

    axios({
      method: "post",
      url: quoteRequestFormEndpoint,
      data: formDataWithFiles,
    })
      .then((r) => {
        const email = form.email.value;
        const phone = form.tel.value;
        handleServerResponse(true, "Thanks!", form);
        window.uetq = window.uetq || [];
        window.uetq.push("event", "submit_lead_form", {});
        dataLayer.push({
          event: 'formSubmit',
          formName: 'careerApplicationForm',
          userEmail: email,
          userPhone: phone
        })
        navigate(`/ssf-confirmation-page`);
      })
      .catch((r) => {
        // eslint-disable-next-line no-console
        console.error(r)
        handleServerResponse(false, r?.response?.data?.error ?? r, form);
      });
  };

  const rand = useMemo(() => randomstring.generate(5), []);

  return (
    <FormWrapper>
      <h2>
        <HtmlContent htmlString={siteData.wp.siteSettings.siteSettings.quoteFormHeading} />
      </h2>
      <p>
        <HtmlContent htmlString={siteData.wp.siteSettings.siteSettings.quoteFormSubheading} />
      </p>

      <form onSubmit={handleOnSubmit} encType="multipart/form-data">
        <Grid columnGap={inline ? "" : "tight"} rowGap="16px">
          <Grid item md={inline ? "6" : ""} columnGap={inline ? "" : "tight"}>
            <input
              type="hidden"
              name="source"
              id={`${rand}_source`}
              value={source ? source : "No Source"}
              hidden
            />
            <input
              type="hidden"
              name="googleClientId"
              id={`${rand}_googleClientId`}
              value={getGAClientId()}
              hidden
            />
            <FormField>
              <input
                type="text"
                name="name"
                id={`${rand}_name`}
                aria-describedby="name"
                placeholder="Name"
                required
              />
              <label htmlFor={`${rand}_name`}>Name</label>
            </FormField>
            <FormField>
              <input
                type="email"
                name="email"
                id={`${rand}_email`}
                aria-describedby="email"
                required
                placeholder="Email"
              />
              <label htmlFor={`${rand}_email`}>Email</label>
            </FormField>
            <FormField>
              <input
                type="tel"
                name="tel"
                id={`${rand}_tel`}
                aria-describedby="tel"
                required
                placeholder="Phone number"
              />
              <label htmlFor={`${rand}_tel`}>Phone number</label>
            </FormField>
          </Grid>
          <Grid item md={inline ? "6" : ""} columnGap={inline ? "" : "tight"}>
            <FormField>
              <input
                type="text"
                name="company"
                id={`${rand}_company`}
                aria-describedby="company"
                placeholder="Company (optional)"
              />
              <label htmlFor={`${rand}_company`}>Company (optional)</label>
            </FormField>
            <FormField>
              <textarea
                type="text"
                name="requirements"
                id={`${rand}_requirements`}
                aria-describedby="requirements"
                placeholder="Requirements"
                required
              />
              <label htmlFor={`${rand}_requirements`}>Requirements</label>
            </FormField>
          </Grid>
          <Grid item columnGap={inline ? "" : "tight"}>
            <FileInput
              files={files}
              setFiles={setFiles}
              error={fileError}
              setError={setFileError}
            />
          </Grid>
        </Grid>
        <FormFooterStyles $inline={inline}>
          <div>
            <ButtonStyles
              type="submit"
              disabled={serverState.submitting || !!fileError}
              className="quote-submit-btn"
            >
              Request quote
            </ButtonStyles>
            <p className="no-js">Please enable Javascript to continue</p>
            {serverState.status && (
              <p className={!serverState.status.ok ? "errorMsg" : ""}>
                {serverState.status.msg}
              </p>
            )}
          </div>
          <ContactTelephoneWrapper>
            <h3>
              Or call now <CallTrackLink />
            </h3>
          </ContactTelephoneWrapper>
        </FormFooterStyles>
      </form>
    </FormWrapper>
  );
};
